import { mt4Tutorials } from '@/mocks/mt4mt5TutorialsMt4';
import { mt5Tutorials } from '@/mocks/mt4mt5TutorialsMt5';
import { tutorialDetailsMt4StartChart } from '@/mocks/tutorialDetailsMt4StartChart';
import { tutorialDetailsMt4ToolsTrading } from '@/mocks/tutorialDetailsMt4ToolsTrading';
import { tutorialDetailsMt5StartChart } from '@/mocks/tutorialDetailsMt5StartChart';
import { tutorialDetailsMt5OrderTrouble } from '@/mocks/tutorialDetailsMt5OrderTrouble';
import type { Article } from '@/utils/articleTypes';
import type { TutorialDetailMap } from '@/utils/tutorialTypes';

export type Platform = 'MT4' | 'MT5';

type TutorialSeed = {
  slug: string;
  group: string;
  title: string;
  minutes: number;
  desc: string;
  steps: string[];
  note: string;
};

export type TutorialGroup = {
  key: string;
  label: string;
  icon: string;
  articles: Article[];
};

const tutorialImages: Record<string, string> = {
  'MT4-start':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20desktop%20trading%20platform%20window%20on%20a%20light%20mint%20background%2C%20left%20sidebar%20list%20of%20currency%20rows%2C%20one%20main%20chart%20panel%20with%20simple%20teal%20candlesticks%2C%20thin%20warm%20orange%20trend%20lines%2C%20clean%20grid%2C%20soft%20shadows%2C%20no%20text%2C%20no%20people%2C%20no%20logos%2C%20editorial%20fintech%20style&width=1200&height=800&seq=mt4-screen-start-01&orientation=landscape',
  'MT5-start':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20modern%20multi%20panel%20trading%20platform%20on%20a%20light%20mint%20background%2C%20several%20small%20chart%20tiles%20with%20teal%20candles%2C%20a%20narrow%20market%20list%20on%20the%20left%2C%20warm%20orange%20accents%2C%20clean%20grid%2C%20no%20text%2C%20no%20people%2C%20no%20logos%2C%20editorial%20fintech%20style&width=1200&height=800&seq=mt5-screen-start-01&orientation=landscape',
  'MT4-chart':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20large%20candlestick%20chart%20window%20on%20a%20light%20mint%20background%2C%20teal%20candles%2C%20one%20diagonal%20warm%20orange%20trend%20line%20drawn%20across%20the%20chart%2C%20small%20toolbar%20of%20line%20tools%20at%20the%20top%2C%20clean%20grid%2C%20no%20text%2C%20no%20people%2C%20editorial%20fintech%20style&width=1200&height=800&seq=mt4-screen-chart-01&orientation=landscape',
  'MT5-chart':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20modern%20chart%20window%20on%20a%20light%20mint%20background%2C%20teal%20candles%20with%20a%20moving%20average%20line%20in%20warm%20orange%2C%20small%20object%20list%20panel%20on%20the%20right%2C%20clean%20grid%2C%20no%20text%2C%20no%20people%2C%20no%20logos%2C%20editorial%20fintech%20style&width=1200&height=800&seq=mt5-screen-chart-01&orientation=landscape',
  'MT4-indicator':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20trading%20platform%20with%20an%20indicator%20settings%20dialog%20open%20over%20a%20chart%2C%20muted%20teal%20chart%20in%20the%20background%2C%20small%20list%20of%20indicator%20names%20on%20the%20left%2C%20warm%20orange%20highlight%20on%20one%20row%2C%20light%20mint%20background%2C%20no%20text%2C%20no%20people%2C%20editorial%20fintech%20style&width=1200&height=800&seq=mt4-screen-indicator-01&orientation=landscape',
  'MT5-tools':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20toolbox%20panel%20with%20an%20indicator%20library%20list%2C%20small%20search%20field%20at%20the%20top%2C%20teal%20accents%20and%20warm%20orange%20selection%2C%20chart%20preview%20behind%20the%20panel%2C%20light%20mint%20background%2C%20no%20text%2C%20no%20people%2C%20no%20logos%2C%20editorial%20fintech%20style&width=1200&height=800&seq=mt5-screen-tools-01&orientation=landscape',
  'MT4-order':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20an%20order%20ticket%20window%20with%20volume%20field%20and%20two%20large%20buy%20and%20sell%20buttons%20in%20teal%20and%20warm%20orange%2C%20a%20simple%20candlestick%20chart%20behind%20it%2C%20light%20mint%20background%2C%20no%20text%2C%20no%20people%2C%20no%20logos%2C%20editorial%20fintech%20style&width=1200&height=800&seq=platform-screen-order-01&orientation=landscape',
  'MT5-order':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20modern%20order%20entry%20panel%20with%20stop%20loss%20and%20take%20profit%20fields%2C%20teal%20fields%20and%20warm%20orange%20confirm%20button%2C%20small%20chart%20preview%20above%2C%20light%20mint%20background%2C%20no%20text%2C%20no%20people%2C%20editorial%20fintech%20style&width=1200&height=800&seq=mt5-screen-order-01&orientation=landscape',
  'MT4-trouble':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20trading%20platform%20with%20a%20disconnected%20state%2C%20empty%20chart%20area%20in%20soft%20grey%20teal%2C%20a%20small%20warm%20orange%20warning%20mark%20beside%20the%20connection%20indicator%2C%20light%20mint%20background%2C%20clean%20grid%2C%20no%20text%2C%20no%20people%2C%20editorial%20fintech%20style&width=1200&height=800&seq=platform-screen-trouble-01&orientation=landscape',
  'MT5-trouble':
    'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20trading%20platform%20chart%20loading%20placeholder%20with%20soft%20teal%20skeleton%20lines%2C%20small%20warm%20orange%20refresh%20arrow%2C%20light%20mint%20background%2C%20clean%20minimal%20composition%2C%20no%20text%2C%20no%20people%2C%20no%20logos%2C%20editorial%20fintech%20style&width=1200&height=800&seq=mt5-screen-trouble-01&orientation=landscape',
};

const fallbackImage =
  'https://readdy.ai/api/search-image?query=Minimal%20flat%20illustration%20of%20a%20trading%20platform%20window%20with%20a%20soft%20teal%20candlestick%20chart%20and%20warm%20orange%20accent%20line%20on%20a%20light%20mint%20background%2C%20clean%20editorial%20fintech%20style%2C%20no%20text%2C%20no%20people%2C%20no%20logos&width=1200&height=800&seq=platform-screen-fallback-01&orientation=landscape';

const groupMeta: Record<Platform, Record<string, { label: string; icon: string }>> = {
  MT4: {
    start: { label: 'เริ่มต้นใช้งาน MT4', icon: 'ri-flag-line' },
    chart: { label: 'การใช้งานกราฟ MT4', icon: 'ri-line-chart-line' },
    indicator: { label: 'อินดิเคเตอร์ MT4', icon: 'ri-pulse-line' },
    order: { label: 'คำสั่งซื้อขาย MT4', icon: 'ri-exchange-dollar-line' },
    trouble: { label: 'แก้ไขปัญหา MT4', icon: 'ri-error-warning-line' },
  },
  MT5: {
    start: { label: 'เริ่มต้นใช้งาน MT5', icon: 'ri-flag-line' },
    chart: { label: 'การจัดการกราฟ MT5', icon: 'ri-line-chart-line' },
    tools: { label: 'อินดิเคเตอร์และเครื่องมือ MT5', icon: 'ri-tools-line' },
    order: { label: 'คำสั่งซื้อขาย MT5', icon: 'ri-exchange-dollar-line' },
    trouble: { label: 'แก้ไขปัญหา MT5', icon: 'ri-error-warning-line' },
  },
};

const groupOrder: Record<Platform, string[]> = {
  MT4: ['start', 'chart', 'indicator', 'order', 'trouble'],
  MT5: ['start', 'chart', 'tools', 'order', 'trouble'],
};

const platformGuides: Record<Platform, string> = {
  MT4: 'mt4-guide',
  MT5: 'mt5-guide',
};

const tutorialDetails: TutorialDetailMap = {
  ...tutorialDetailsMt4StartChart,
  ...tutorialDetailsMt4ToolsTrading,
  ...tutorialDetailsMt5StartChart,
  ...tutorialDetailsMt5OrderTrouble,
};

function buildArticles(platform: Platform, seeds: TutorialSeed[]): Article[] {
  return seeds.map((seed, index) => {
    const meta = groupMeta[platform][seed.group];
    const groupLabel = meta ? meta.label : `${platform}`;
    const siblings = seeds
      .filter((item) => item.group === seed.group && item.slug !== seed.slug)
      .slice(0, 2)
      .map((item) => item.slug);
    const detail = tutorialDetails[seed.slug];

    const fallbackSections = [
      {
        id: `steps-${index + 1}`,
        heading: 'ขั้นตอนที่ควรทำตาม',
        paragraphs: [
          'ทำตามลำดับทีละขั้นบนบัญชีทดลองก่อน หากขั้นไหนทำไม่ได้ให้หยุดและตรวจสอบก่อน เพราะการข้ามขั้นมักทำให้ค่าที่ตั้งไว้ไม่ตรงกับที่ต้องการ',
        ],
        bullets: seed.steps,
        steps: [],
      },
      {
        id: `notes-${index + 1}`,
        heading: 'ข้อควรระวัง',
        paragraphs: [seed.note],
        bullets: [],
        steps: [],
      },
    ];

    return {
      slug: seed.slug,
      category: 'MT4 / MT5',
      categorySlug: 'mt4-mt5',
      title: seed.title,
      description: seed.desc,
      readingTime: `${seed.minutes} นาที`,
      updatedDate: '2026-09-15',
      level: 'เริ่มเทรด',
      heroImage: tutorialImages[`${platform}-${seed.group}`] ?? fallbackImage,
      tags: [platform, groupLabel],
      intro: detail
        ? detail.intro
        : [
            seed.desc,
            `บทความนี้อยู่ในหมวด${groupLabel} ของคู่มือ ${platform} ในหน้านี้ เนื้อหาอ้างอิงหน้าจอมาตรฐานของแพลตฟอร์ม ถ้าตำแหน่งเมนูในเวอร์ชันที่คุณใช้ต่างออกไปเล็กน้อย ให้ยึดโครงสร้างหลักตามลำดับขั้นตอนด้านล่าง`,
          ],
      sections: detail
        ? detail.sections.map((section, sIndex) => ({
            id: `section-${sIndex + 1}`,
            heading: section.heading,
            paragraphs: section.paragraphs,
            bullets: section.bullets ?? [],
            steps: [],
          }))
        : fallbackSections,
      faqs: detail ? detail.faqs : [],
      related: [...siblings, platformGuides[platform]].slice(0, 3),
    };
  });
}

export const mt4TutorialArticles = buildArticles('MT4', mt4Tutorials);
export const mt5TutorialArticles = buildArticles('MT5', mt5Tutorials);
export const tutorialArticles: Article[] = [...mt4TutorialArticles, ...mt5TutorialArticles];

function buildGroups(platform: Platform, articles: Article[]): TutorialGroup[] {
  const seeds: TutorialSeed[] = platform === 'MT4' ? mt4Tutorials : mt5Tutorials;
  return groupOrder[platform].map((key) => {
    const meta = groupMeta[platform][key];
    const groupArticles = seeds
      .filter((seed) => seed.group === key)
      .map((seed) => articles.find((article) => article.slug === seed.slug))
      .filter((article): article is Article => Boolean(article));
    return {
      key,
      label: meta.label,
      icon: meta.icon,
      articles: groupArticles,
    };
  });
}

export const mt4TutorialGroups = buildGroups('MT4', mt4TutorialArticles);
export const mt5TutorialGroups = buildGroups('MT5', mt5TutorialArticles);